import CashPrice from '@components/application/pricing/cashPrice';
import Deposit from '@components/application/pricing/deposit';
import MonthlyPayment from '@components/application/pricing/monthlyPayment';
import Saving from '@components/application/pricing/saving';
import { SearchVehicle } from '@javascript/types/Vehicle';
import classNames from 'classnames';

interface SearchPricingProps {
  pricing: SearchVehicle['salesInfo']['pricing'];
  stockReference?: string;
  useShowcasePricingPadding?: boolean;
  vehicleUrl?: string;
}

const SearchPricing = ({
  pricing,
  stockReference,
  useShowcasePricingPadding,
  vehicleUrl,
}: SearchPricingProps) => {
  const hideCashPrice =
    pricing.cashPrice === 0 &&
    Boolean(
      pricing.businessVehicle ||
      pricing.businessContractHire ||
      pricing.personalContractHire,
    );

  const showMonthlyPayment = Boolean(pricing.monthlyPayment);
  const showExtras = Boolean(pricing.extras);
  const showAdvancePaymentExtras = pricing.extras === 'Advance payment';
  const showDividerAfterCash = showMonthlyPayment && !hideCashPrice;
  const showDividerBeforeExtras =
    showExtras && showMonthlyPayment && hideCashPrice;

  const hasCashPriceSuffix = Boolean(pricing.cashPriceSuffix?.trim());
  const hasSaving = Boolean(pricing.saving);
  const shouldUseShowcasePadding = Boolean(useShowcasePricingPadding);
  const useShowcaseSavingPadding =
    shouldUseShowcasePadding && hasSaving && hasCashPriceSuffix;
  const useShowcaseSuffixPadding =
    shouldUseShowcasePadding &&
    !useShowcaseSavingPadding &&
    (hasSaving || hasCashPriceSuffix);

  let showcaseSuffixPaddingClassName: string | undefined;

  if (useShowcaseSavingPadding) {
    showcaseSuffixPaddingClassName = 'ac-pricing--showcase-suffix-saving';
  } else if (useShowcaseSuffixPadding) {
    showcaseSuffixPaddingClassName = 'ac-pricing--showcase-suffix';
  }

  const useWideFinanceSpacing =
    !hideCashPrice &&
    showMonthlyPayment &&
    (pricing.cashPricePrefix === 'Expected retail price' ||
      pricing.cashPrice >= 100000);

  const pricingClassName = classNames(
    'ac-pricing ch-display--flex ch-flex-flow--row-wrap ch-fw--500 ch-pv--3',
    showcaseSuffixPaddingClassName,
    {
      'ac-pricing--contract-hire': pricing.businessContractHire,
      'ac-pricing--with-cash': !hideCashPrice,
      'ac-pricing--with-finance': showMonthlyPayment,
      'ac-pricing--without-finance': !showMonthlyPayment,
      'ac-pricing--with-extras': showExtras,
      'ac-pricing--finance-spacing-wide': useWideFinanceSpacing,
    },
  );

  return (
    <div className="ch-mb--3 ch-bt--1 ch-bb--1 ch-bc--grey-3 ch-display--flex ch-flex-flow--column-wrap ch-mh--2 ch-justify-content--center">
      <div className={pricingClassName}>
        <div className="ac-pricing__row ch-display--flex ch-flex-flow--row-nowrap ch-width--12">
          {!hideCashPrice && (
            <CashPrice
              amount={pricing.cashPrice}
              prefix={pricing.cashPricePrefix}
              suffix={pricing.cashPriceSuffix}
            />
          )}
          {showDividerAfterCash && (
            <div className="ac-pricing__divider ch-flex--1" />
          )}
          {showMonthlyPayment && (
            <div className="result__finance ch-display--flex ch-flex--none ch-align-items--center ch-flex-flow--row-nowrap ch-justify-content--start">
              <div className="result__finance-content ch-display--flex ch-flex-flow--row-nowrap ch-align-items--center">
                <Deposit
                  amount={pricing.deposit ?? 0}
                  isBusinessContractHire={pricing.businessContractHire}
                  isPersonalContractHire={pricing.personalContractHire}
                />
                <span className="ac-pricing__plus ch-fs--3">+</span>
                <MonthlyPayment
                  amount={pricing.monthlyPayment ?? 0}
                  showTooltip
                  stockReference={stockReference}
                  vehicleUrl={vehicleUrl}
                />
              </div>
            </div>
          )}
          {showDividerBeforeExtras && (
            <div className="ac-pricing__divider ch-flex--1" />
          )}
          {showExtras && (
            <span className="ac-pricing__extras ch-display--flex ch-justify-content--start ch-align-items--end ch-text--center">
              {showAdvancePaymentExtras ? (
                <span className="ch-display--flex ch-flex-flow--column-nowrap ch-lh--body">
                  <span>Advance</span>
                  <span>payment</span>
                </span>
              ) : (
                pricing.extras
              )}
            </span>
          )}
        </div>
        {pricing.saving && (
          <Saving
            amount={pricing.saving}
            prefix={pricing.savingPrefix}
            suffix={pricing.savingSuffix}
          />
        )}
      </div>
    </div>
  );
};

export default SearchPricing;
